import { Spinner, Text } from "@fluentui/react-components";
import * as pdfjs from "pdfjs-dist";
import PdfWorker from "pdfjs-dist/build/pdf.worker.min.mjs?worker";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Document, Page } from "react-pdf";
import { usePdfDocumentPreviewStyles } from "./PdfDocumentPreview.styles";

export interface IPdfDocumentPreviewProps {
  pdfUrl: string | null;
  disableScroll?: boolean;
}

// Vite's ?worker import bundles the worker as a plain .js asset, so it does not
// depend on the web server serving .mjs with a JavaScript MIME type. Created
// lazily so no worker is spawned until a PDF is actually rendered.
const ensurePdfWorker = (): void => {
  if (pdfjs.GlobalWorkerOptions.workerPort || typeof Worker === "undefined") {
    return;
  }
  pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker();
};

export const PdfDocumentPreview = ({
  pdfUrl,
  disableScroll,
}: IPdfDocumentPreviewProps): JSX.Element => {
  ensurePdfWorker();
  const styles = usePdfDocumentPreviewStyles();
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoadSuccess = useCallback((pdf: { numPages: number }) => {
    setTotalPages(pdf.numPages);
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleLoadError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
  }, []);

  // Generate array of page numbers for rendering
  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_v, i) => i + 1);
  }, [totalPages]);

  useEffect(() => {
    // Reset state when URL changes
    setIsLoading(true);
    setHasError(false);
    setTotalPages(0);
  }, [pdfUrl]);

  if (!pdfUrl) {
    return (
      <div className={styles.loadingContainer} role="status" aria-live="polite">
        <Spinner label="Loading PDF..." />
      </div>
    );
  }

  if (hasError) {
    return (
      <div className={styles.errorContainer} role="alert">
        <Text className={styles.errorHeading}>Unable to load PDF</Text>
        <Text className={styles.errorBody}>
          The PDF could not be loaded. Please try again or contact support.
        </Text>
      </div>
    );
  }

  return (
    <div className={disableScroll ? styles.rootNoScroll : styles.root}>
      {isLoading && (
        <div
          className={styles.initialLoadingContainer}
          role="status"
          aria-live="polite"
        >
          <Spinner label="Loading PDF document..." />
        </div>
      )}
      <div
        className={
          disableScroll ? styles.noScrollContainer : styles.scrollContainer
        }
      >
        <Document
          file={pdfUrl}
          onLoadSuccess={handleLoadSuccess}
          onLoadError={handleLoadError}
          loading={
            <div className={styles.documentLoadingContainer}>
              <Spinner size="small" />
            </div>
          }
        >
          {pageNumbers.map((pageNumber) => (
            <div key={pageNumber} className={styles.pageWrapper}>
              <Page pageNumber={pageNumber} />
            </div>
          ))}
        </Document>
      </div>
    </div>
  );
};
